/**
 * Shared helpers for the MediSpa landing-page entrypoints
 * (frontend/entrypoints/medispa-lp-*.ts), which are loaded conditionally by
 * the theme's deferred-scripts.liquid snippet at the end of <body>.
 */
import type Swiper from 'swiper';

/** Runs the callback once the DOM is parsed (immediately if it already is). */
export function onDomReady(callback: () => void): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback);
  } else {
    callback();
  }
}

/**
 * Runs the callback once the global Swiper bundle is available. Swiper loads
 * via a deferred classic script rendered by deferred-scripts.liquid ahead of
 * the entrypoints, so it is normally already defined; the load-event fallback
 * covers late execution (e.g. theme editor section reloads).
 */
export function whenSwiperReady(callback: () => void): void {
  const run = (): void => {
    if (typeof window.Swiper !== 'undefined') {
      callback();
      return;
    }

    const swiperScript = document.querySelector<HTMLScriptElement>(
      'script[data-medispa-lp-swiper]',
    );

    if (swiperScript) {
      swiperScript.addEventListener('load', callback, { once: true });
    }
  };

  onDomReady(run);
}

/** Returns the shared window.Medispa.carousels registry, creating it if needed. */
export function medispaCarousels(): Record<string, Swiper> {
  if (!window.Medispa) {
    window.Medispa = { carousels: {} };
  }

  if (!window.Medispa.carousels) {
    window.Medispa.carousels = {};
  }

  return window.Medispa.carousels;
}
